import { Reveal } from "./Reveal";

const Overline = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8C4A32] mb-4">{children}</p>
);

const echoes = [
  "\u201CIf you're good at the store, you can have a treat.\u201D",
  "Saying \u201Cdon't be stingy\u201D when he grabs all the bread.",
  "Your toddlers fighting over bath bubbles — with millions of them right there.",
  "Trying so hard to be GENTLE that you end up with zero boundaries.",
];

export const Story = () => (
  <>
    <section data-testid="the-moment-section" className="py-16 sm:py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <Overline>The moment</Overline>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-[#241E1A] font-medium">
              Your mother's voice. Out of your mouth.
            </h2>
          </Reveal>
        </div>
        <div className="md:col-span-7 space-y-6 text-base sm:text-lg leading-relaxed text-[#5C524A]">
          <Reveal delay={0.1}>
            <p>
              You read the books. You did the inner child work. You meditate, you journal,
              you raised your standards and built a life you're proud of.
            </p>
            <p className="mt-4">Then your baby became a toddler.</p>
            <p className="mt-4">
              She reached for something in the Target aisle, and before you could stop it,
              you heard it:
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <blockquote className="border-l-2 border-[#8C4A32] pl-6 py-2 font-serif-display italic text-2xl sm:text-3xl text-[#241E1A] leading-snug">
              "No, baby, that's too expensive."
            </blockquote>
            <p className="mt-4">Your mother's voice. Out of your mouth. Or it shows up another way:</p>
          </Reveal>
          <Reveal delay={0.3}>
            <ul className="space-y-3 mt-2">
              {echoes.map((e, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#C69C6D] shrink-0" />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 font-medium text-[#241E1A]">
              You didn't do all the inner work just to pass the old story down.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <section data-testid="thesis-section" className="py-16 sm:py-24 bg-[#241E1A] text-[#FBF8F3]">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-7 md:col-start-4">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C69C6D] mb-6">The truth</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] font-medium">
              It's not your mindset. <em className="italic text-[#C69C6D]">It's your words.</em>
            </h2>
            <div className="mt-8 space-y-5 text-base sm:text-lg leading-relaxed text-[#FBF8F3]/75">
              <p>You don't need more mindset work. You've done it. This is a language problem.</p>
              <p>
                Most of us grew up on <strong className="text-[#FBF8F3] font-semibold">fake poor language</strong>:
                the little phrases that teach a child there isn't enough, that wanting is too much,
                and that love is earned by being good.
              </p>
              <p>
                Those phrases still run on autopilot — especially when you're tired. Nobody ever
                taught you what to say instead.
              </p>
              <p className="font-serif-display italic text-xl sm:text-2xl text-[#C69C6D]">
                It's not your fault. And it's very fixable.
              </p>
              <p>Your beliefs changed your life. Your words are what reach her.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section data-testid="why-now-section" className="py-16 sm:py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="md:col-span-5 order-2 md:order-1">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-[#EFE6D8] shadow-[0_24px_60px_-24px_rgba(36,30,26,0.35)]">
              <img
                src="/photos/photo1.jpg"
                alt="Shellie with her daughter Helena at the beach"
                className="w-full h-full object-cover aspect-[4/5]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-7 order-1 md:order-2">
          <Reveal>
            <Overline>Why now</Overline>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-[#241E1A] font-medium">
              The ages 1–5 window only opens once.
            </h2>
            <div className="mt-6 space-y-5 text-base sm:text-lg leading-relaxed text-[#5C524A]">
              <p>
                Between ages 1 and 5, children learn language by soaking up the words and tone
                around them. They repeat phrases long before they understand them — and the
                phrases they hear most become their inner dialogue.
              </p>
              <p className="font-medium text-[#241E1A]">
                That's the beautiful part. Change the words now, and the rest of these years
                become the foundation she grows from — not something she has to undo later.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </>
);
